import { Controller, useFormContext } from 'react-hook-form';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { Input, Spinner } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { CustomerExistenceResult } from '@/api/customers';
import type { ExistenceState } from './use-existence-check';
import type { CustomerWizardValues } from './wizard-form';

/** Human-readable reasons the email is already taken (mirrors legacy messages). */
function existsReasons(r: CustomerExistenceResult): string[] {
  const msgs: string[] = [];
  if (r.exists_in_customers) msgs.push('This email already belongs to a customer.');
  if (r.exists_in_customer_contacts) msgs.push('This email is used by an existing customer contact.');
  if (r.exists_in_contact_sources) msgs.push('This email is already a marketing lead.');
  if (msgs.length === 0) msgs.push('This email already exists.');
  return msgs;
}

/**
 * Step 1 — enter the customer email and verify it is unique before continuing.
 * The debounced check runs in the page; `email` renders a status icon + inline
 * errors, and `exists` blocks the Continue button (enforced by the page).
 */
export function StepCheckEmail({ email }: { email: ExistenceState }) {
  const { control, formState } = useFormContext<CustomerWizardValues>();
  const fieldError = formState.errors.customer?.customer_email?.message;
  const result = email.result;
  const exists = !!result?.exists;
  const valid = !email.checking && !!result && !exists;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)', maxWidth: 480 }}>
      <Controller
        control={control}
        name="customer.customer_email"
        render={({ field }) => (
          <Field
            label="Customer email"
            required
            error={
              fieldError ||
              (exists ? existsReasons(result!).join(' ') : undefined)
            }
            description="We'll check this address isn't already a customer or lead."
          >
            {(p) => (
              <Input
                {...p}
                {...field}
                type="email"
                placeholder="name@company.com"
                autoFocus
                invalid={!!fieldError || exists}
                endSlot={
                  email.checking ? (
                    <Spinner size="sm" />
                  ) : exists ? (
                    <AlertCircle size={16} style={{ color: 'var(--vn-danger-solid)' }} />
                  ) : valid ? (
                    <CheckCircle2 size={16} style={{ color: 'var(--vn-success-solid)' }} />
                  ) : null
                }
              />
            )}
          </Field>
        )}
      />
      {valid ? (
        <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-success-text)' }}>
          Email is available.
        </span>
      ) : null}
    </div>
  );
}
